import { useCallback, ReactEventHandler } from 'react';
import { RedirectionSideEffect, useNotify, useRedirect } from 'react-admin';

import { DeleteBranchParams, TreeRecord } from '../types';
import { useDeleteBranch } from '../dataProvider';
import { UseMutationOptions } from 'react-query';

/**
 * Prepare callback for a delete branch button with undo support
 *
 * @example
 *
 * import React from 'react';
 * import ActionDelete from '@material-ui/icons/Delete';
 * import { Button, useDeleteBranchWithUndoController } from '@react-admin/ra-tree';
 *
 * const DeleteButton = ({
 *     resource,
 *     record,
 *     basePath,
 *     redirect,
 *     onClick,
 *     ...rest
 * }) => {
 *     const [handleDelete, { isLoading }] = useDeleteBranchWithUndoController({
 *         resource,
 *         record,
 *         redirect,
 *     });
 *
 *     const handleClick = () => {
 *         handleDelete();
 *     }
 *
 *     return (
 *         <Button
 *             onClick={handleClick}
 *             disabled={isLoading}
 *             label="ra.action.delete"
 *             {...rest}
 *         >
 *             <ActionDelete />
 *         </Button>
 *     );
 * };
 */
export const useDeleteBranchWithUndoController = <
    RecordType extends TreeRecord = TreeRecord
>({
    mutationOptions,
    record,
    redirect: redirectTo = 'list',
    resource,
}: UseDeleteBranchWithUndoControllerParams<RecordType>): UseDeleteBranchWithUndoControllerReturn => {
    const notify = useNotify();
    const redirect = useRedirect();

    const [deleteBranch, { isLoading }] = useDeleteBranch<RecordType>();
    const handleDelete = useCallback(
        event => {
            event.stopPropagation();

            deleteBranch(
                resource,
                {
                    id: record.id,
                    previousData: record,
                },
                {
                    onSuccess: () => {
                        notify('ra.notification.deleted', {
                            type: 'info',
                            messageArgs: { smart_count: 1 },
                            undoable: true,
                        });
                        redirect(redirectTo, resource);
                    },
                    onError: error =>
                        notify(
                            typeof error === 'string'
                                ? error
                                : error.message || 'ra.notification.http_error',
                            { type: 'warning' }
                        ),
                    mutationMode: 'undoable',
                    ...mutationOptions,
                }
            );
        },
        [
            deleteBranch,
            mutationOptions,
            notify,
            record,
            redirect,
            redirectTo,
            resource,
        ]
    );

    return { handleDelete, isLoading };
};

export interface UseDeleteBranchWithUndoControllerParams<
    RecordType extends TreeRecord = TreeRecord
> {
    mutationOptions?: UseMutationOptions<
        RecordType,
        Error,
        DeleteBranchParams<RecordType>
    >;
    record?: RecordType;
    redirect?: RedirectionSideEffect;
    resource: string;
}

export interface UseDeleteBranchWithUndoControllerReturn {
    isLoading: boolean;
    handleDelete: ReactEventHandler<any>;
}
